// What the staff screens say, in the reader's language.
//
// English is written here, in group files under phrases/ (one per screen, each key
// prefixed with its group); a task that adds a group adds one line below. Every
// other language is a file, i18n/locales/<code>.json (docs/i18n.md). Lookup and
// fallback are i18n/catalog.ts: a missing translation says the English, never the key.
import { admin } from './phrases/admin'
import { appointments, bookings } from './phrases/appointments'
import { audit } from './phrases/audit'
import { booking } from './phrases/booking'
import { bookingsReport } from './phrases/bookingsReport'
import { directory, floorBoard } from './phrases/batch3'
import { calling } from './phrases/calling'
import { catalogue } from './phrases/catalogue'
import { common } from './phrases/common'
import { connect } from './phrases/connect'
import { deliveries, webhooks } from './phrases/connections'
import { displays } from './phrases/displays'
import { counter } from './phrases/counter'
import { entry } from './phrases/entry'
import { equipment } from './phrases/equipment'
import { estimate } from './phrases/estimate'
import { errors } from './phrases/errors'
import { feed } from './phrases/feed'
import { feedbackAdmin, feedbackReport } from './phrases/feedback'
import { floor } from './phrases/floor'
import { hall } from './phrases/hall'
import { insight } from './phrases/insight'
import { network } from './phrases/network'
import { people } from './phrases/people'
import { profiles } from './phrases/profiles'
import { notify } from './phrases/notify'
import { org } from './phrases/org'
import { settingsOrg } from './phrases/settingsOrg'
import { reception } from './phrases/reception'
import { record } from './phrases/record'
import { pivot } from './phrases/pivot'
import { reportMail } from './phrases/reportMail'
import { reports } from './phrases/reports'
import { roles } from './phrases/roles'
import { branchPage } from './phrases/branchPage'
import { screenApps } from './phrases/screenApps'
import { settings } from './phrases/settings'
import { signage } from './phrases/signage'
import { designs } from './phrases/designs'
import { tickets } from './phrases/tickets'
import { tokens } from './phrases/tokens'
import { ui } from './phrases/ui'
import { wording } from './phrases/wording'
import { shippedLanguages, sayWord } from './i18n/catalog'
import { LANGUAGES, SOURCE_LANGUAGE, type LanguageCode } from './i18n/languages'
import { monthName, weekdayName } from './i18n/format'
import { legacyIndex, placeholdersIn, type Vars } from './i18n/plural'

/** A staff screen's language: any code in i18n/languages.ts. */
export type StaffLanguage = LanguageCode

/** The staff UI is English only for now (redesign 2026-10); flip to bring other languages back. */
export const ENGLISH_ONLY: boolean = true

/** The languages a staff screen can be read in: English and each with a shipped file, in menu order. */
export const STAFF_LANGUAGES: readonly StaffLanguage[] = LANGUAGES.map((l) => l.code).filter(
  (code) => code === SOURCE_LANGUAGE || shippedLanguages().includes(code),
)

/** Each language named in itself, which is how a person finds their own. */
export const LANGUAGE_NAMES: Record<StaffLanguage, string> = Object.fromEntries(LANGUAGES.map((l) => [l.code, l.native]))

/** A language named in itself; its code when the build does not know it. */
export function languageName(code: StaffLanguage): string {
  return LANGUAGE_NAMES[code] ?? code
}

/** English, plus the dormant az/ru written beside it before languages became files (i18n/locales/). */
export type Phrase = { en: string; az?: string; ru?: string }

export const phrases = {
  ...common,
  ...entry,
  ...errors,
  ...floor,
  ...counter,
  ...reception,
  ...estimate,
  ...reports,
  ...pivot,
  ...reportMail,
  ...audit,
  ...admin,
  ...catalogue,
  ...record,
  ...calling,
  ...floorBoard,
  ...feedbackAdmin,
  ...feedbackReport,
  ...directory,
  ...webhooks,
  ...deliveries,
  ...notify,
  ...bookings,
  ...appointments,
  ...booking,
  ...bookingsReport,
  ...hall,
  ...network,
  ...org,
  ...settingsOrg,
  ...displays,
  ...signage,
  ...designs,
  ...screenApps,
  ...branchPage,
  ...connect,
  ...ui,
  ...people,
  ...insight,
  ...roles,
  ...tickets,
  ...feed,
  ...profiles,
  ...equipment,
  ...settings,
  ...tokens,
  ...wording,
} satisfies Record<string, Phrase>

export type StaffKey = keyof typeof phrases
export type { Vars }

/** A language a staff screen can be read in: English or one with a shipped file. */
export function isStaffLanguage(value: unknown): value is StaffLanguage {
  return typeof value === 'string' && STAFF_LANGUAGES.includes(value)
}

/** Whether a key built at runtime — from a server's code — is one we have words for. */
export function hasPhrase(key: string): key is StaffKey {
  return Object.prototype.hasOwnProperty.call(phrases, key)
}

/** Which of a legacy phrase's "a|b|c" forms to use (Intl.PluralRules, i18n/plural.ts). */
export const pluralIndex: (lang: StaffLanguage, n: number, forms: number) => number = legacyIndex

/**
 * t returns the phrase in the chosen language, with its placeholders filled
 * (i18n/catalog.ts: organisation's words, the language file, then English).
 *
 * Plurals are ICU ("{n, plural, one {# queue} other {# queues}}", i18n/plural.ts).
 * Legacy "a|b" forms choose by vars.count when given, else vars.n: count is for a
 * number shown already formatted ("12,345") or a noun agreeing with another number.
 */
export function t(key: StaffKey, lang: StaffLanguage, vars?: Vars): string {
  const phrase: Phrase = phrases[key]
  return sayWord(key, lang, phrase.en, vars, legacyOf(phrase, lang))
}

/** A dormant az/ru beside the English, read when that language's file lacks the key. */
function legacyOf(phrase: Phrase, lang: StaffLanguage): string | undefined {
  return lang === 'az' ? phrase.az : lang === 'ru' ? phrase.ru : undefined
}

/** The placeholders a phrase needs, in English. */
export function placeholdersOf(key: StaffKey): string[] {
  return placeholdersIn(phrases[key].en)
}

/**
 * A catalogue name — a service, a queue, an outcome — in the reader's language.
 *
 * English second, because it was the only language staff screens read before
 * there was a choice; then Azerbaijani, which is the one an organisation actually
 * fills in; then Russian; then whatever the row is otherwise called.
 */
export function nameIn(
  name: Record<string, string | undefined> | undefined,
  lang: StaffLanguage,
  fallback: string,
): string {
  return name?.[lang] || name?.en || name?.az || name?.ru || fallback
}

/** A moment written for a reader of this language: names from Intl in the reader's
 *  language (never the machine's), tables where the runtime lacks it (i18n/format.ts).
 *  The clock is 24-hour, as every screen here writes it.
 *
 *  Without a zone, or with '', the clock is the reader's own. With an IANA zone
 *  (a branch's timezone), the moment is written as a clock in that zone shows it,
 *  so a booking at a branch in Baku reads the same from a laptop in London. A zone
 *  this browser does not know is written as if none were given. */
export function formatMoment(
  at: string | Date,
  lang: StaffLanguage,
  parts: { date?: boolean; time?: boolean; weekday?: boolean; zone?: string },
): string {
  const d = typeof at === 'string' ? new Date(at) : at
  if (Number.isNaN(d.getTime())) return typeof at === 'string' ? at : ''
  const clock = (parts.zone ? wallClock(d, parts.zone) : undefined) ?? localClock(d)
  const out: string[] = []
  if (parts.weekday) out.push(weekdayName(lang, clock.weekday0))
  if (parts.date) out.push(`${clock.day} ${monthName(lang, clock.month0)}`)
  const pad = (n: number) => String(n).padStart(2, '0')
  if (parts.time) out.push(`${pad(clock.hour)}:${pad(clock.minute)}`)
  return out.join(', ')
}

/** A moment as a clock and calendar show it: the numbers, never the words. */
interface WallClock {
  month0: number
  day: number
  weekday0: number
  hour: number
  minute: number
}

function localClock(d: Date): WallClock {
  return { month0: d.getMonth(), day: d.getDate(), weekday0: d.getDay(), hour: d.getHours(), minute: d.getMinutes() }
}

// Asked only for numbers in a named zone; en-US so the weekday parses below. One formatter per zone.
const zoneFormats = new Map<string, Intl.DateTimeFormat>()
const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** The moment on a clock in this zone, or undefined for a zone this browser does not know. */
function wallClock(d: Date, zone: string): WallClock | undefined {
  let format = zoneFormats.get(zone)
  if (!format) {
    try {
      format = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
      })
    } catch {
      // An unknown zone throws a RangeError. The caller falls back to the reader's clock.
      return undefined
    }
    zoneFormats.set(zone, format)
  }
  const found: Record<string, string> = {}
  for (const part of format.formatToParts(d)) found[part.type] = part.value
  const weekday0 = SHORT_WEEKDAYS.indexOf(found.weekday ?? '')
  const month = Number(found.month)
  const day = Number(found.day)
  // h23 should never say 24, but an engine that does means midnight.
  const hour = Number(found.hour) % 24
  const minute = Number(found.minute)
  if (weekday0 < 0 || ![month, day, hour, minute].every(Number.isFinite)) return undefined
  return { month0: month - 1, day, weekday0, hour, minute }
}

/** How long something has lasted, as a clock on a busy screen shows it. */
export function formatDuration(seconds: number, lang: StaffLanguage): string {
  if (seconds < 60) return t('common.durSec', lang, { s: seconds })
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return t('common.durMinSec', lang, { m: minutes, s: String(seconds % 60).padStart(2, '0') })
  }
  return t('common.durHourMin', lang, {
    h: Math.floor(minutes / 60),
    m: String(minutes % 60).padStart(2, '0'),
  })
}

/** A limit set in whole minutes (a service's time limit), said as minutes. Unlike
 *  formatDuration, which is for something that is still running and genuinely has
 *  seconds on its clock, a limit never does — it was typed as a number of minutes
 *  — and running it through formatDuration said "1m 00s" for a limit of one
 *  minute, a precision the person who set it never gave. */
export function formatMinuteLimit(seconds: number, lang: StaffLanguage): string {
  return t('common.durMin', lang, { m: Math.round(seconds / 60) })
}

/** A customer's language as a report names it, in the reader's language. */
export const SPOKEN: Record<string, Record<StaffLanguage, string>> = {
  az: { en: 'Azerbaijani', az: 'Azərbaycan dili', ru: 'Азербайджанский' },
  ru: { en: 'Russian', az: 'Rus dili', ru: 'Русский' },
  en: { en: 'English', az: 'İngilis dili', ru: 'Английский' },
  tr: { en: 'Turkish', az: 'Türk dili', ru: 'Турецкий' },
}

/**
 * A business date, 2026-09-15, as a person says it.
 *
 * Read from its parts rather than handed to new Date(text), which would read it as
 * UTC midnight and name the previous day anywhere west of Greenwich.
 */
export function dayName(businessDate: string, lang: StaffLanguage): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(businessDate)
  if (!parts) return businessDate
  const [, year, month, day] = parts
  const at = new Date(Number(year), Number(month) - 1, Number(day))
  if (Number.isNaN(at.getTime())) return businessDate
  const weekday = weekdayName(lang, at.getDay())
  const monthWord = monthName(lang, at.getMonth())
  const date = at.getDate()
  switch (lang) {
    case 'ru':
      return `${weekday}, ${date} ${monthWord}`
    case 'az':
      return `${date} ${monthWord}, ${weekday}`
    default:
      return `${weekday} ${date} ${monthWord}`
  }
}
